/**
 * WeeklyDigestCard (Iter 33) — pipeline-view summary of the consultant's
 * heat-map share-link activity. Sits on the Workspaces dashboard.
 *
 * Renders the same data shape the weekly email uses, so what the consultant
 * sees in-app is byte-for-byte what their inbox copy looks like.
 *
 * Two interaction points:
 *   - "Send to my inbox" button → POST /api/digests/me/send (fires the email
 *     via Resend; in stub mode it just logs).
 *   - "View..." links on share rows that route to the workspace's heat-map
 *     sub-tab (the obvious next step from the digest).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  EnvelopeSimpleIcon as Envelope,
  EyeIcon as Eye,
  ClockIcon as Clock,
  ChartLineUpIcon as ChartLineUp,
  CircleNotchIcon as Spinner,
  ArrowsClockwiseIcon as ArrowsClockwise,
  BellIcon as Bell,
  BellSlashIcon as BellSlash,
} from "@phosphor-icons/react";
import { getMyDigestPreview, sendMyDigest, nudgeChaseCandidates, snoozeShare } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/auth";
import NudgeModal from "@/components/sapecc/NudgeModal";

const LOOKBACK_PRESETS = [
  { days: 7,  label: "7d" },
  { days: 14, label: "14d" },
  { days: 30, label: "30d" },
];

function Kpi({ label, value, color, testId }) {
  return (
    <div className="ml-card p-3 text-center" data-testid={testId}>
      <div className="ml-mono text-[9px] uppercase tracking-widest text-[var(--ml-text-secondary)]">{label}</div>
      <div className="text-xl font-semibold mt-1" style={{ color }}>
        {(value ?? 0).toLocaleString()}
      </div>
    </div>
  );
}

// Iter 34.c — outcome chip styling per bulk-nudge result status.
const OUTCOME_CHIPS = {
  sent: {
    label: "Nudged",
    bg: "#defbe6", fg: "#0e6027", border: "#a7f0ba",
  },
  cooldown: {
    label: "Cooldown",
    bg: "#fff8e1", fg: "#8a6c00", border: "#f1c21b",
  },
  no_email: {
    label: "No email",
    bg: "#f4f4f4", fg: "#525252", border: "#c6c6c6",
  },
  no_write_access: {
    label: "No access",
    bg: "#f4f4f4", fg: "#525252", border: "#c6c6c6",
  },
  revoked: {
    label: "Revoked",
    bg: "#fff1f1", fg: "#a2191f", border: "#ffd7d9",
  },
  expired: {
    label: "Expired",
    bg: "#fff1f1", fg: "#a2191f", border: "#ffd7d9",
  },
  snoozed: {
    label: "Snoozed",
    bg: "#edf5ff", fg: "#0043ce", border: "#a6c8ff",
  },
  not_found: {
    label: "Missing",
    bg: "#f4f4f4", fg: "#525252", border: "#c6c6c6",
  },
};

function OutcomeChip({ outcome }) {
  if (!outcome) return null;
  // `outcome` is the per-share result entry from the bulk endpoint:
  // either {status: 'sent', ...} or {status: 'skipped', reason: 'cooldown'|'no_email'|...}.
  const key = outcome.status === "sent" ? "sent" : outcome.reason || "skipped";
  const cfg = OUTCOME_CHIPS[key] || OUTCOME_CHIPS.no_email;
  return (
    <span
      className="ml-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 border whitespace-nowrap"
      style={{ background: cfg.bg, color: cfg.fg, borderColor: cfg.border }}
      data-testid={`digest-outcome-chip-${outcome.token_suffix}`}
      data-outcome={key}
      title={
        outcome.status === "sent"
          ? `Reminder email sent to ${outcome.recipient_email || "recipient"}.`
          : `Skipped — ${(outcome.reason || "unknown").replace(/_/g, " ")}` +
            (outcome.hours_remaining ? ` (try in ~${outcome.hours_remaining}h).` : ".")
      }
    >
      {cfg.label}
    </span>
  );
}

function ShareRow({ row, kind, onNudge, onSnooze, outcome }) {
  // Each digest row carries workspace_id so the heat-map sub-tab is one click away.
  const recipient = row.recipient_email || row.recipient_label || (
    <span className="text-[var(--ml-text-secondary)] italic">(no recipient)</span>
  );
  // Iter 34 — chase rows surface a Nudge icon button when an email is available.
  // The Nudge action is intercepted before the Link navigation fires.
  const canNudge = Boolean(onNudge && row.recipient_email && row.token);
  // Iter 34.d — chase rows also expose a one-click Snooze (7d) so a consultant
  // can suppress a deliberately-held-back chase candidate without nudging it.
  const canSnooze = Boolean(onSnooze && row.token);
  const handleNudgeClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onNudge(row);
  };
  const handleSnoozeClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onSnooze(row);
  };
  return (
    <Link
      to={`/workspaces/${row.workspace_id}/sap-ecc`}
      className="flex items-center gap-3 px-3 py-2 border-b border-[var(--ml-border)] last:border-0 hover:bg-[var(--ml-surface)]/40 transition-colors"
      data-testid={`digest-${kind}-row`}
    >
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-sm truncate">{row.workspace_name}</div>
        <div className="text-xs text-[var(--ml-text-secondary)] truncate">
          {recipient}
        </div>
      </div>
      {/* Iter 34.c — flash a per-row outcome chip for ~5s after a bulk nudge run. */}
      {outcome && <OutcomeChip outcome={outcome} />}
      <div className="ml-mono text-xs text-[var(--ml-text-secondary)] flex items-center gap-1 whitespace-nowrap">
        <Eye size={11} weight="bold" /> {row.view_count ?? 0}
      </div>
      {canNudge && (
        <button
          type="button"
          onClick={handleNudgeClick}
          data-testid={`digest-${kind}-nudge-${row.token_suffix}`}
          className="ml-btn-ghost p-1.5 text-[var(--ml-primary)]"
          title={
            (row.nudges_sent ?? 0) > 0
              ? `Nudge again (already sent ${row.nudges_sent} time${row.nudges_sent === 1 ? "" : "s"})`
              : "Send a reminder email to this recipient"
          }
        >
          <Bell size={12} weight="bold" />
        </button>
      )}
      {canSnooze && (
        <button
          type="button"
          onClick={handleSnoozeClick}
          data-testid={`digest-${kind}-snooze-${row.token_suffix}`}
          className="ml-btn-ghost p-1.5 text-[var(--ml-text-secondary)]"
          title="Snooze this row for 7 days (hide from chase candidates)"
        >
          <BellSlash size={12} weight="bold" />
        </button>
      )}
    </Link>
  );
}

function Section({ title, description, rows, kind, emptyMsg, onNudge, onSnooze, headerAction, outcomesByToken }) {
  return (
    <div className="ml-card">
      <div className="px-3 py-2 border-b border-[var(--ml-border)] flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]" data-testid={`digest-section-${kind}-title`}>
            {title}
            {rows.length > 0 && (
              <span className="ml-1 normal-case text-[var(--ml-text)]">· {rows.length}</span>
            )}
          </div>
          {description && (
            <div className="text-xs text-[var(--ml-text-secondary)] mt-0.5">{description}</div>
          )}
        </div>
        {headerAction && (
          <div className="shrink-0">{headerAction}</div>
        )}
      </div>
      {rows.length === 0 ? (
        <div className="px-3 py-4 text-xs text-[var(--ml-text-secondary)] italic" data-testid={`digest-section-${kind}-empty`}>
          {emptyMsg}
        </div>
      ) : (
        <div data-testid={`digest-section-${kind}-rows`}>
          {rows.map((r, i) => (
            <ShareRow
              key={`${r.workspace_id}-${r.token_suffix || i}`}
              row={r}
              kind={kind}
              onNudge={onNudge}
              onSnooze={onSnooze}
              outcome={outcomesByToken?.[r.token_suffix]}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function WeeklyDigestCard() {
  const [digest, setDigest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lookback, setLookback] = useState(7);
  const [sending, setSending] = useState(false);
  // Iter 34 — nudge modal state. We carry workspace_id alongside the share so
  // the modal can hit the right tenant-scoped endpoint without poking digest data.
  const [nudgeTarget, setNudgeTarget] = useState(null); // { workspaceId, share }
  // Iter 34.b — bulk nudge ("Nudge all chase candidates") in-flight flag.
  const [bulkNudging, setBulkNudging] = useState(false);
  // Iter 34.c — per-row outcome chips that flash for ~5s after a bulk nudge.
  // Keyed by token_suffix → outcome entry returned by the bulk endpoint.
  const [bulkOutcomes, setBulkOutcomes] = useState({});

  const fetchDigest = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const d = await getMyDigestPreview(lookback);
      setDigest(d);
    } catch (e) {
      setError(formatApiErrorDetail(e) || "Failed to load digest");
    } finally {
      setLoading(false);
    }
  }, [lookback]);

  useEffect(() => {
    fetchDigest();
  }, [fetchDigest]);

  // Iter 34.c — auto-clear the per-row outcome chips ~5s after a bulk run so
  // the row reverts to its normal layout. Tests can extend this by mocking
  // setTimeout, but the simple-timer approach is fine for a single ephemeral UI.
  useEffect(() => {
    if (!Object.keys(bulkOutcomes).length) return undefined;
    const t = setTimeout(() => setBulkOutcomes({}), 5000);
    return () => clearTimeout(t);
  }, [bulkOutcomes]);

  const handleSend = async () => {
    setSending(true);
    try {
      const res = await sendMyDigest(lookback);
      const msg = res.stubbed
        ? "Digest queued (stub mode — check backend logs)."
        : "Digest sent to your inbox.";
      toast.success(msg);
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Failed to send digest");
    } finally {
      setSending(false);
    }
  };

  // Iter 34.d — one-click snooze (7d) from a chase row.
  const handleSnoozeChaseRow = async (row) => {
    if (!row?.workspace_id || !row?.token) return;
    try {
      await snoozeShare(row.workspace_id, row.token, 7);
      toast.success(`Snoozed for 7 days — will reappear after ${new Date(Date.now() + 7 * 86400 * 1000).toISOString().slice(0, 10)}.`);
      fetchDigest();
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Failed to snooze");
    }
  };

  // Iter 34.b — single-click bulk nudge across every chase candidate.
  // The backend respects per-share guardrails (cooldown, no_email) and reports
  // them as ``skipped`` in the response — we surface a one-line summary toast.
  const chaseCandidates = digest?.chase_candidates || [];
  const nudgeableCount = chaseCandidates.filter(
    (r) => r.recipient_email && (!r.last_nudge_at || Date.now() - new Date(r.last_nudge_at).getTime() >= 24 * 60 * 60 * 1000),
  ).length;
  const handleBulkNudge = async () => {
    if (!window.confirm(
      `Send a reminder email to ${nudgeableCount} chase recipient${nudgeableCount === 1 ? "" : "s"}? ` +
      `(Shares in cooldown or without an email are skipped.)`,
    )) return;
    setBulkNudging(true);
    try {
      const res = await nudgeChaseCandidates(lookback);
      const parts = [];
      if (res.sent > 0) parts.push(`${res.sent} sent`);
      if (res.skipped > 0) parts.push(`${res.skipped} skipped`);
      const summary = parts.join(" · ") || "Nothing to nudge";
      toast.success(`Bulk nudge complete — ${summary}.`);
      // Iter 34.c — index outcomes by token_suffix and flash chips for 5s.
      const byToken = {};
      (res.results || []).forEach((r) => {
        byToken[r.token_suffix] = r;
      });
      setBulkOutcomes(byToken);
      // Refresh digest so cooldown state stays accurate.
      fetchDigest();
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Failed to send bulk nudge");
    } finally {
      setBulkNudging(false);
    }
  };

  const totals = digest?.totals || {};
  const period = digest?.period || {};

  const dateRange = useMemo(() => {
    if (!period.start || !period.end) return "";
    return `${period.start.slice(0, 10)} → ${period.end.slice(0, 10)}`;
  }, [period.start, period.end]);

  return (
    <div className="space-y-4" data-testid="weekly-digest-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] flex items-center gap-1.5 mb-1">
            <ChartLineUp size={11} weight="bold" /> Weekly digest
          </div>
          <h3 className="text-lg font-semibold tracking-tight">Pipeline overview</h3>
          {dateRange && (
            <p className="text-xs text-[var(--ml-text-secondary)] ml-mono">{dateRange}</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <div
            className="flex gap-0.5 border border-[var(--ml-border)]"
            role="radiogroup"
            data-testid="digest-lookback-bar"
          >
            {LOOKBACK_PRESETS.map((p) => (
              <button
                key={p.days}
                type="button"
                onClick={() => setLookback(p.days)}
                data-testid={`digest-lookback-${p.days}`}
                className={`px-2 py-1 text-xs ml-mono ${lookback === p.days ? "bg-[var(--ml-primary)] text-white" : "hover:bg-[var(--ml-surface)]/40"}`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={fetchDigest}
            data-testid="digest-refresh-btn"
            className="ml-btn-icon"
            title="Refresh"
          >
            <ArrowsClockwise size={14} weight="bold" />
          </button>
          <button
            type="button"
            onClick={handleSend}
            disabled={sending || loading}
            data-testid="digest-send-btn"
            className="ml-btn-primary whitespace-nowrap"
            title="Email this digest to your inbox"
          >
            {sending ? <Spinner size={12} className="animate-spin" /> : <Envelope size={12} weight="bold" />}
            <span>{sending ? "Sending…" : "Email me"}</span>
          </button>
        </div>
      </div>

      {loading && (
        <div className="ml-card p-6 text-center text-sm text-[var(--ml-text-secondary)]" data-testid="digest-loading">
          <Spinner size={14} className="animate-spin inline mr-2" />Computing digest…
        </div>
      )}

      {error && (
        <div className="ml-card p-3 text-sm text-[#a2191f]" data-testid="digest-error">{error}</div>
      )}

      {!loading && !error && digest && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="digest-kpis">
            <Kpi label="Active" value={totals.active_shares} testId="digest-kpi-active" />
            <Kpi label="Created" value={totals.shares_created_in_period} testId="digest-kpi-created" />
            <Kpi label="Views" value={totals.total_views_in_period} testId="digest-kpi-views" />
            <Kpi label="Unopened" value={totals.unopened_active_shares} color={totals.unopened_active_shares > 0 ? "#8a6c00" : undefined} testId="digest-kpi-unopened" />
            <Kpi label="Expiring 7d" value={totals.expiring_within_7d} color={totals.expiring_within_7d > 0 ? "#a2191f" : undefined} testId="digest-kpi-expiring" />
          </div>

          <Section
            title="Top engagements"
            rows={digest.top_shares || []}
            kind="top"
            emptyMsg="No active shares yet — mint one from any SAP ECC workspace's Heat map sub-tab."
          />

          <Section
            title="Chase candidates"
            description="Active shares ≥3 days old that haven't been opened. Worth a follow-up nudge."
            rows={digest.chase_candidates || []}
            kind="chase"
            emptyMsg="All caught up — every share has been opened or is still fresh."
            onNudge={(row) =>
              setNudgeTarget({
                workspaceId: row.workspace_id,
                share: row,
              })
            }
            onSnooze={handleSnoozeChaseRow}
            outcomesByToken={bulkOutcomes}
            headerAction={
              nudgeableCount > 0 ? (
                <button
                  type="button"
                  onClick={handleBulkNudge}
                  disabled={bulkNudging}
                  data-testid="digest-bulk-nudge-btn"
                  className="ml-btn-secondary text-xs whitespace-nowrap"
                  title={`Send a reminder to all ${nudgeableCount} eligible chase recipient${nudgeableCount === 1 ? "" : "s"}`}
                >
                  {bulkNudging ? (
                    <><Spinner size={11} className="animate-spin" /> Nudging…</>
                  ) : (
                    <><Bell size={11} weight="bold" /> Nudge all ({nudgeableCount})</>
                  )}
                </button>
              ) : null
            }
          />

          <Section
            title="Expiring within 7 days"
            description={<span className="inline-flex items-center gap-1"><Clock size={10} weight="bold" /> Re-issue these soon if they&rsquo;re still relevant.</span>}
            rows={digest.expiring_soon || []}
            kind="expiring"
            emptyMsg="No shares expiring in the next 7 days."
          />
        </>
      )}

      <NudgeModal
        workspaceId={nudgeTarget?.workspaceId}
        share={nudgeTarget?.share}
        open={Boolean(nudgeTarget)}
        onClose={() => setNudgeTarget(null)}
        onSent={() => {
          // Refresh the digest so chase_candidates / nudges_sent stay accurate.
          fetchDigest();
        }}
      />
    </div>
  );
}
